/**
 * Dot-path access into a form's value object (`"vehicle.make"`,
 * `"parts.0.partNumber"`). Pure, no DOM dependency — part of the
 * framework-agnostic core (schemaWalk.ts's own doc comment explains why that
 * boundary matters).
 */

export function getValueAtPath(values: unknown, path: string): unknown {
  if (path === '') {
    return values;
  }

  return path.split('.').reduce<unknown>((current, segment) => {
    if (current === null || typeof current !== 'object') {
      return undefined;
    }
    return (current as Record<string, unknown>)[segment];
  }, values);
}

/** Returns a NEW object with `path` set to `value` — never mutates `values`. */
export function setValueAtPath(
  values: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const segments = path.split('.');
  return setAtSegments(values, segments, value) as Record<string, unknown>;
}

function setAtSegments(current: unknown, segments: string[], value: unknown): unknown {
  const [head, ...rest] = segments;
  if (head === undefined) {
    return value;
  }

  // A purely-numeric segment is a repeating-group row index — the container
  // at this level must be a real array (ajv validates `type: array` against
  // one; an object with numeric-looking keys is not the same thing), not an
  // object literal that merely looks array-ish.
  if (/^\d+$/.test(head)) {
    const currentArray = Array.isArray(current) ? [...current] : [];
    const index = Number(head);
    currentArray[index] = rest.length === 0 ? value : setAtSegments(currentArray[index], rest, value);
    return currentArray;
  }

  const currentObject =
    current !== null && typeof current === 'object' && !Array.isArray(current)
      ? (current as Record<string, unknown>)
      : {};

  if (rest.length === 0) {
    return { ...currentObject, [head]: value };
  }

  return {
    ...currentObject,
    [head]: setAtSegments(currentObject[head], rest, value),
  };
}
